import { makeAutoObservable } from "mobx";
import { GroupedOption } from "../../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped";
import { RESOLUTION_CATEGORY } from "../../../../models/ResolutionTypes";
import { IResolutionCategoryPreset, IResolutionPreset } from "../../policy-types";
import { RESOLUTION_TYPE } from "../../../resolutions/constants";
import dashboardSettingsStore from "../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../../models/ProfileCapabilities";

const REFUND_RESOLUTION_TYPES = new Set<RESOLUTION_TYPE>([
    RESOLUTION_TYPE.Credits,
    RESOLUTION_TYPE.PlatformStoreCreditType,
    RESOLUTION_TYPE.GiftCard,
    RESOLUTION_TYPE.Refund,
    RESOLUTION_TYPE.BankRefund,
    RESOLUTION_TYPE.InstantRefundType,
]);

class ResolutionsPreset {
    private _model: IResolutionCategoryPreset[] = [] as IResolutionCategoryPreset[];

    constructor(model: IResolutionCategoryPreset[]) {
        makeAutoObservable(this, {}, { autoBind: true });
        this._model = model;
    } 

    public get resolutionsList(): IResolutionPreset[] {
        const list: IResolutionPreset[] = [];
        this._model.forEach((c: IResolutionCategoryPreset) => {
            c.list.forEach((r: IResolutionPreset) => {
                list.push(r);
            });
        });

        return list;
    }

    public getPresetById(merchantResolutionId: number): IResolutionPreset|undefined {
        return this.resolutionsList.find(r => r.id === merchantResolutionId);
    }

    public getOptionsPreset(): GroupedOption[] {
        const textByCategoryMap = {
            [RESOLUTION_CATEGORY.exchange_resolutions]: {
                title: 'Exchange',
            },
            [RESOLUTION_CATEGORY.standard_resolutions]: {
                title: 'Return Resolutions',
            },
            [RESOLUTION_CATEGORY.returngo_resolutions]: {
                title: 'ReturnScore Instant Credit',
            },
            [RESOLUTION_CATEGORY.cancel_order_resolutions]: {
                title: 'Order Modification',
            },
            [RESOLUTION_CATEGORY.other_resolutions]: { /** this category is not fetched from BE */
                title: 'Other', 
            },
        }

        return this._model.map((group: IResolutionCategoryPreset) => ({
            category: textByCategoryMap[group.category].title,
            options: group.list
            .filter((resolution) => {
	            const product = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.PRODUCT_EXCHANGE);
	            const manual = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.MANUAL_EXCHANGE);
	            const variant = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.VARIANT_EXCHANGE);
                const refundResolutionCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.REFUND_RESOLUTION);

                const resolutionType = resolution.resolution_type as RESOLUTION_TYPE;
                const isUnsupportedRefundResolution =
                    REFUND_RESOLUTION_TYPES.has(resolutionType) &&
                    !(refundResolutionCapability.supportedRefundTypes as readonly RESOLUTION_TYPE[]).includes(resolutionType);

	            return !(
		            (resolutionType === RESOLUTION_TYPE.ProductExchangeType && !product.isSupported) ||
		            (resolutionType === RESOLUTION_TYPE.DirectVariantExchange && !variant.isSupported) ||
		            (resolutionType === RESOLUTION_TYPE.ManualExchangeType && !manual.isSupported) ||
                    isUnsupportedRefundResolution
	            );
            })
	        .map((resolution) => ({
                label: resolution.internal_name,
                value: resolution.id
            }))
        }));
    }
}

export default ResolutionsPreset;
